import type { Product, VariationProduct } from '@frontend/libs/types/product'

import { useProductStore } from '@frontend/libs/stores/useProductStore'
import { type Image, ImageSize } from '@frontend/libs/types/media'
import { isMobileDevice } from '@frontend/libs/utils'
import { Button, cn } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import WishlistButton from '../../../shop/wishlist/WishlistButton'
import NewArrival from '../NewArrival'
import { ProductImage } from '../ProductImage'
import ProductPrice from '../ProductPrice'
import Swatch, { type SelectedOption } from '../swatch/Swatch'

export interface ProductItemProps {
  product: Product
  variant?: 'default' | 'wishlist'
}

function findVariationProduct(
  variations: VariationProduct[],
  options: SelectedOption,
): undefined | VariationProduct {
  return variations.find((variation) => {
    const terms = variation.terms
    return Object.keys(options).every((key) => terms[key] === options[key])
  })
}

export function ProductItem({
  product,
  variant = 'default',
}: Readonly<ProductItemProps>) {
  const {
    id,
    image,
    isNewArrival,
    name,
    options,
    price,
    regularPrice,
    salePrice,
    secondImage,
    url,
    variations,
  } = product

  const [selectedOption, setSelectedOption] = useState<SelectedOption>(
    {} as SelectedOption,
  )
  const [variation, setVariation] = useState<null | VariationProduct>(null)
  const { openQuickView } = useProductStore((state) => state)

  function handleOnSelectOption(values: SelectedOption) {
    const newOptions = { ...selectedOption, ...values }
    const newVariation = findVariationProduct(variations, newOptions)

    setSelectedOption(newVariation?.terms || newOptions)
    setVariation(newVariation)
  }

  const selectedImage: Image = variation?.image || image
  const selectedSecondImage: Image = variation?.secondImage || secondImage

  const maxDisplay = isMobileDevice ? 3 : 5

  const renderProductOptions = () =>
    options?.map((option) => (
      <Swatch
        key={option.slug}
        maxDisplay={maxDisplay}
        onClick={handleOnSelectOption}
        option={option}
        value={selectedOption[option.slug]}
      />
    ))

  return (
    <div>
      <figure className="product-item--figure">
        <Link to={variation?.url || url}>
          <ProductImage image={selectedImage} size={ImageSize.Medium} />
          <ProductImage
            className="img-hover"
            image={selectedSecondImage}
            size={ImageSize.Medium}
          />
        </Link>
        {!isMobileDevice && (
          <div className="product-item--quick-view">
            <Button
              className="!hover:opacity-100 bg-white"
              color="default"
              onClick={() => openQuickView(product, selectedOption)}
              radius="full"
            >
              {__('Quick View', 'asavagroup')}
            </Button>
          </div>
        )}
      </figure>
      <div className="product-item-content">
        <h2 className="mb-2 flex justify-between">
          <span className="font-medium uppercase">
            {isNewArrival && <NewArrival />}
            <Link className="line-clamp-1" to={variation?.url || url}>
              {name}
            </Link>
          </span>
          <WishlistButton productId={id} variant="icon" />
        </h2>
        <ProductPrice
          price={price}
          regularPrice={regularPrice}
          salePrice={salePrice}
        />
        <div className="mt-4 flex items-end justify-between">
          {variant === 'wishlist' && (
            <span
              className={cn('block text-sm font-medium uppercase', {
                'text-green-500': product.stockStatus === 'instock',
                'text-red-500': product.stockStatus === 'outofstock',
              })}
            >
              {product.stockStatus
                ? __('In stock', 'asavagroup')
                : __('Out of stock', 'asavagroup')}
            </span>
          )}
          {variant === 'default' && renderProductOptions()}
        </div>
      </div>
    </div>
  )
}

export default ProductItem
